import { ToggleContentEvent } from "../events/ToggleContentEvent.js";

/**
 * Class to implement the toggle-content functionality
 * for a single instance; the application is done
 * via the ToggleContentManager class
 */
export class ToggleContent {
    private identifier: string | null = null;
    private content: HTMLDivElement | null = null;
    private button: HTMLButtonElement | null = null;
    private label: HTMLSpanElement | null = null;

    /**
     * Constructor of the ToggleContent
     *
     * @param {HTMLElement} scope
     */
    public constructor(scope: HTMLElement) {
        this.button = scope.querySelector<HTMLButtonElement>(
            `:scope > button:last-of-type`,
        );
        this.identifier = this.button?.getAttribute("aria-controls") ?? null;
        this.content = scope.querySelector<HTMLDivElement>(
            `#${this.identifier}`,
        );
        this.label =
            this.button?.querySelector<HTMLSpanElement>("span") ?? null;

        this.useMouseClick();
        this.useStateUpdates();
    }

    /**
     * Handles the state of the toggleable
     * content via mouse-click
     *
     * @returns {void}
     */
    protected useMouseClick(): void {
        this.button?.addEventListener("click", () => {
            switch (this.button?.getAttribute("aria-expanded")) {
                case "true":
                    document.dispatchEvent(
                        new ToggleContentEvent({
                            detail: {
                                action: "close",
                                identifier: this.identifier,
                            },
                        }),
                    );
                    break;

                case "false":
                    document.dispatchEvent(
                        new ToggleContentEvent({
                            detail: {
                                action: "open",
                                identifier: this.identifier,
                            },
                        }),
                    );
                    break;
            }
        });
    }

    /**
     * Handles updates to the state of the target
     * element and reflects them on all elements
     *
     * @returns {void}
     */
    protected useStateUpdates(): void {
        document.addEventListener(ToggleContentEvent.KEY, (event) => {
            if (event instanceof ToggleContentEvent) {
                const { action, identifier } = event.detail;
                if (!this.identifier || this.identifier !== identifier) return;

                switch (action) {
                    case "open":
                        this.button?.setAttribute("aria-expanded", "true");
                        if (this.content) this.content.hidden = false;

                        if (this.label) {
                            this.label.textContent =
                                this.button?.dataset.labelCollapse ?? "";
                        }
                        break;

                    case "close":
                        this.button?.setAttribute("aria-expanded", "false");
                        if (this.content) this.content.hidden = true;

                        if (this.label) {
                            this.label.textContent =
                                this.button?.dataset.labelExpand ?? "";
                        }
                        break;
                }
            }
        });
    }
}
